/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\NotificationsTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Account Menu module.
----------------------------------------------------------------*/

import { Moon, Sun, Bell, Circle } from 'lucide-react';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import type { AppearanceForm } from '../types';

interface NotificationsTabProps {
    watchedValues: any;
    register: UseFormRegister<AppearanceForm>;
    setValue: UseFormSetValue<AppearanceForm>;
}

export function NotificationsTab({ watchedValues, register, setValue }: NotificationsTabProps) {
    const isDarkMode = watchedValues.mode === 'dark';
    const themeKey = isDarkMode ? 'darkTheme' : 'lightTheme';
    const themeLabel = isDarkMode ? 'Dark Theme Notifications' : 'Light Theme Notifications';
    const ThemeIcon = isDarkMode ? Moon : Sun;

    const notificationTypes = [
        { key: 'successColor', label: 'Success', defaultLight: '#22c55e', defaultDark: '#22c55e' },
        { key: 'errorColor', label: 'Error', defaultLight: '#ef4444', defaultDark: '#ef4444' },
        { key: 'warningColor', label: 'Warning', defaultLight: '#f59e0b', defaultDark: '#f59e0b' },
        { key: 'infoColor', label: 'Info', defaultLight: '#3b82f6', defaultDark: '#3b82f6' }
    ];

    return (
        <FormBox title="Notification Colors" icon={<Bell />}>
            <div>
                <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-2">
                    <ThemeIcon className="h-4 w-4" />
                    {themeLabel}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {notificationTypes.map((type) => (
                        <div key={type.key}>
                            <SimpleTooltip content={`${type.label} notification color for banners and alerts.`} position="top">
                                <label className="block text-sm font-medium text-foreground mb-2 cursor-default w-fit">
                                    {type.label}
                                </label>
                            </SimpleTooltip>
                            <div className="flex gap-2">
                                <input
                                    {...register(`notifications.${themeKey}.${type.key}` as any)}
                                    type="color"
                                    className="w-12 h-10 rounded border border-input cursor-pointer p-0 overflow-hidden bg-transparent"
                                    onChange={(e) => {
                                        setValue(`notifications.${themeKey}.${type.key}` as any, e.target.value);
                                    }}
                                />
                                <input
                                    {...register(`notifications.${themeKey}.${type.key}` as any)}
                                    type="text"
                                    placeholder={isDarkMode ? type.defaultDark : type.defaultLight}
                                    className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                                            setValue(`notifications.${themeKey}.${type.key}` as any, value);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Live Preview */}
            <div className="mt-6">
                <h3 className="text-sm font-medium text-foreground mb-4">Live Preview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl" style={{
                    backgroundColor: watchedValues.mode === 'dark' ? 'hsl(var(--background) / 0.8)' : 'hsl(var(--background) / 0.8)',
                    border: '1px solid hsl(var(--border))'
                }}>
                    {[
                        { key: 'successColor', label: 'Success Notification', bgOpacity: 0.15, borderOpacity: 0.3 },
                        { key: 'errorColor', label: 'Error Notification', bgOpacity: 0.1, borderOpacity: 0.2 },
                        { key: 'warningColor', label: 'Warning Notification', bgOpacity: 0.1, borderOpacity: 0.2 },
                        { key: 'infoColor', label: 'Info Notification', bgOpacity: 0.1, borderOpacity: 0.2 }
                    ].map((type) => {
                        const isDarkMode = watchedValues.mode === 'dark';
                        const themeKey = isDarkMode ? 'darkTheme' : 'lightTheme';
                        const hexColor = watchedValues.notifications?.[themeKey]?.[type.key as 'successColor' | 'errorColor' | 'warningColor' | 'infoColor'] || (type.key === 'successColor' ? '#22c55e' : type.key === 'errorColor' ? '#ef4444' : type.key === 'warningColor' ? '#f59e0b' : '#3b82f6');

                        // Convert hex to rgb string format
                        const hex = hexColor.replace('#', '');
                        const r = parseInt(hex.substring(0, 2), 16) || 0;
                        const g = parseInt(hex.substring(2, 4), 16) || 0;
                        const b = parseInt(hex.substring(4, 6), 16) || 0;
                        const rgb = `${r}, ${g}, ${b}`;

                        return (
                            <div key={type.key} className="flex gap-3 p-4 rounded-xl" style={{
                                background: `linear-gradient(135deg, rgba(${rgb}, ${type.bgOpacity}) 0%, rgba(${rgb}, ${type.bgOpacity * 0.5}) 100%)`,
                                border: `1px solid rgba(${rgb}, ${type.borderOpacity})`,
                                boxShadow: `0 4px 12px rgba(${rgb}, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
                            }}>
                                <div className="flex-shrink-0 mt-0.5 relative flex items-center justify-center">
                                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: hexColor, opacity: 0.2 }} />
                                    <Circle className="w-3 h-3 absolute" style={{ color: hexColor }} strokeWidth={4} />
                                </div>
                                <div>
                                    <div className="font-medium text-sm text-foreground">{type.label}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">Example of {type.label.toLowerCase()} message.</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </FormBox>
    );
}
